import { useEffect, useState } from "react";
import { Clock, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const DAYS = [
  { iso: 1, label: "Mon" }, { iso: 2, label: "Tue" }, { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" }, { iso: 5, label: "Fri" }, { iso: 6, label: "Sat" }, { iso: 7, label: "Sun" },
];
const DEFAULT = { start: "09:00", end: "18:00", days: [1, 2, 3, 4, 5] };

/**
 * WorkingHoursEditor — saves to PATCH /api/me {working_hours: ...}.
 *
 * Used by the cross-timezone availability scanner to know when each teammate
 * is reachable in their own local time.
 */
export default function WorkingHoursEditor({ initial, onSaved, viewerTz }) {
  const [wh, setWh] = useState({ ...DEFAULT, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [pristine, setPristine] = useState(true);

  useEffect(() => { setWh({ ...DEFAULT, ...(initial || {}) }); setPristine(true); }, [initial]);

  const set = (patch) => { setWh((w) => ({ ...w, ...patch })); setPristine(false); };

  const toggleDay = (iso) => {
    setPristine(false);
    setWh((w) => {
      const has = w.days.includes(iso);
      const days = has ? w.days.filter((d) => d !== iso) : [...w.days, iso];
      return { ...w, days: days.sort() };
    });
  };

  const save = async () => {
    if (wh.days.length === 0) { toast.error("Pick at least one working day"); return; }
    if (wh.end <= wh.start) { toast.error("End time must be after start time"); return; }
    setSaving(true);
    try {
      await api.patch("/me", { working_hours: wh });
      setPristine(true);
      onSaved?.();
      toast.success("Working hours saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save working hours");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[var(--border)] p-4" data-testid="working-hours-editor">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={14} className="text-[var(--accent)]" />
        <div className="font-mono text-[10px] tracking-widest text-[var(--text-muted)]">WORKING HOURS</div>
      </div>
      <div className="text-[11px] text-[var(--text-muted)] mb-3">
        Used by the &quot;Find a time&quot; meeting scanner. Times are in your saved timezone ({viewerTz || "your TZ"}).
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[10px] font-mono tracking-widest text-[var(--text-muted)] mb-1">START</label>
          <input type="time" className="input" value={wh.start} onChange={(e) => set({ start: e.target.value })} data-testid="wh-start" />
        </div>
        <div>
          <label className="block text-[10px] font-mono tracking-widest text-[var(--text-muted)] mb-1">END</label>
          <input type="time" className="input" value={wh.end} onChange={(e) => set({ end: e.target.value })} data-testid="wh-end" />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="wh-days">
        {DAYS.map((d) => {
          const on = wh.days.includes(d.iso);
          return (
            <button
              key={d.iso}
              type="button"
              onClick={() => toggleDay(d.iso)}
              className={`px-2.5 py-1.5 text-[11px] font-semibold border transition-colors ${on ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--border)] hover:border-[var(--accent)]"}`}
              data-testid={`wh-day-${d.iso}`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <button onClick={save} disabled={saving || pristine} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wh-save">
        {saving ? "Saving…" : pristine ? (<><CheckCircle size={12} weight="fill" /> Up to date</>) : "Save working hours"}
      </button>
    </div>
  );
}
